const WEEKDAY_OPTIONS = [
    { label: "Mon", value: 1 },
    { label: "Tue", value: 2 },
    { label: "Wed", value: 3 },
    { label: "Thu", value: 4 },
    { label: "Fri", value: 5 },
    { label: "Sat", value: 6 },
    { label: "Sun", value: 0 },
];

const WEEKDAY_NAME_TO_VALUE = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
};

const toTwoDigits = (value) => String(value).padStart(2, "0");

/**
 * Converts a local time (HH:MM) to UTC time (HH:MM)
 * @param {string} localTime - Time in format "HH:MM" (local timezone)
 * @returns {string} - Time in format "HH:MM" (UTC)
 */
const convertLocalTimeToUTC = (localTime) => {
    if (!localTime || typeof localTime !== "string") {
        return "00:00";
    }
    const [hour, minute] = localTime.split(":").map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return "00:00";
    }

    // Create a date object for today at the local time
    const today = new Date();
    const localDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute);
    
    // Get UTC time
    const utcHour = localDate.getUTCHours();
    const utcMinute = localDate.getUTCMinutes();
    
    return `${toTwoDigits(utcHour)}:${toTwoDigits(utcMinute)}`;
};

/**
 * Converts a UTC time (HH:MM) to local time (HH:MM)
 * @param {string} utcTime - Time in format "HH:MM" (UTC)
 * @returns {string} - Time in format "HH:MM" (local timezone)
 */
const convertUTCToLocalTime = (utcTime) => {
    if (!utcTime || typeof utcTime !== "string") {
        return "00:00";
    }
    const [hour, minute] = utcTime.split(":").map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return "00:00";
    }

    // Create a date object for today at UTC time
    const today = new Date();
    const utcDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), hour, minute));
    
    // Get local time
    const localHour = utcDate.getHours();
    const localMinute = utcDate.getMinutes();
    
    return `${toTwoDigits(localHour)}:${toTwoDigits(localMinute)}`;
};

const parseCronParts = (cron) => {
    if (!cron || typeof cron !== "string") {
        return null;
    }

    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) {
        return null;
    }

    return {
        minute: parts[0],
        hour: parts[1],
        dayOfMonth: parts[2],
        month: parts[3],
        dayOfWeek: parts[4],
    };
};

const isNumeric = (value) => /^\d+$/.test(value);

const isAdvancedCron = (parts) => {
    if (!parts) {
        return true;
    }

    const fields = [parts.minute, parts.hour, parts.dayOfMonth, parts.month, parts.dayOfWeek];
    if (fields.some((field) => field.includes("/") || field.includes("?"))) {
        return true;
    }

    if (parts.dayOfMonth !== "*" && parts.dayOfWeek !== "*") {
        return true;
    }

    return false;
};

const normalizeWeekdayList = (rawList) => {
    const values = [];
    const items = rawList.split(",");
    for (const item of items) {
        const trimmed = item.trim();
        if (!trimmed) {
            continue;
        }
        const lower = trimmed.toLowerCase();
        if (WEEKDAY_NAME_TO_VALUE.hasOwnProperty(lower)) {
            values.push(WEEKDAY_NAME_TO_VALUE[lower]);
            continue;
        }
        if (isNumeric(trimmed)) {
            const numeric = Number(trimmed);
            if (numeric === 7) {
                values.push(0);
            } else if (numeric >= 0 && numeric <= 6) {
                values.push(numeric);
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    return values;
};

export const buildCronFromForm = ({ frequency, time, weekdays, dayOfMonth }) => {
    // Convert local time to UTC before building cron
    const utcTime = convertLocalTimeToUTC(time || "00:00");
    const [hour, minute] = utcTime.split(":");
    const cronHour = isNumeric(hour) ? Number(hour) : 0;
    const cronMinute = isNumeric(minute) ? Number(minute) : 0;
    const formattedHour = toTwoDigits(cronHour);
    const formattedMinute = toTwoDigits(cronMinute);

    if (frequency === "daily") {
        return `${formattedMinute} ${formattedHour} * * *`;
    }

    if (frequency === "weekly") {
        const dayList = [...(weekdays || [])];
        const dayPart = dayList.length > 0 ? dayList.join(",") : "*";
        return `${formattedMinute} ${formattedHour} * * ${dayPart}`;
    }

    if (frequency === "monthly") {
        const dom = isNumeric(dayOfMonth) ? Number(dayOfMonth) : 1;
        return `${formattedMinute} ${formattedHour} ${dom} * *`;
    }

    return `${formattedMinute} ${formattedHour} * * *`;
};

export const parseCronToForm = (cron) => {
    const parts = parseCronParts(cron);
    if (!parts) {
        return { isAdvanced: true };
    }

    if (isAdvancedCron(parts)) {
        return { isAdvanced: true };
    }

    const minute = isNumeric(parts.minute) ? Number(parts.minute) : null;
    const hour = isNumeric(parts.hour) ? Number(parts.hour) : null;
    if (minute === null || hour === null) {
        return { isAdvanced: true };
    }

    // Convert UTC time from cron to local time for display
    const utcTime = `${toTwoDigits(hour)}:${toTwoDigits(minute)}`;
    const time = convertUTCToLocalTime(utcTime);

    if (parts.dayOfMonth === "*" && parts.dayOfWeek === "*") {
        return { isAdvanced: false, frequency: "daily", time, weekdays: [], dayOfMonth: "1" };
    }

    if (parts.dayOfMonth === "*" && parts.dayOfWeek !== "*") {
        if (parts.dayOfWeek.includes("-")) {
            return { isAdvanced: true };
        }
        const weekdayValues = normalizeWeekdayList(parts.dayOfWeek);
        if (!weekdayValues || weekdayValues.length === 0) {
            return { isAdvanced: true };
        }
        return { isAdvanced: false, frequency: "weekly", time, weekdays: weekdayValues, dayOfMonth: "1" };
    }

    if (parts.dayOfMonth !== "*" && parts.dayOfWeek === "*") {
        if (parts.dayOfMonth.includes(",") || parts.dayOfMonth.includes("-")) {
            return { isAdvanced: true };
        }
        if (!isNumeric(parts.dayOfMonth)) {
            return { isAdvanced: true };
        }
        const dom = Number(parts.dayOfMonth);
        if (dom < 1 || dom > 31) {
            return { isAdvanced: true };
        }
        return { isAdvanced: false, frequency: "monthly", time, weekdays: [], dayOfMonth: String(dom) };
    }

    return { isAdvanced: true };
};

export const formatScheduleSummary = (cron) => {
    const parsed = parseCronToForm(cron);
    if (parsed.isAdvanced) {
        return "Advanced schedule";
    }

    if (parsed.frequency === "daily") {
        return `Every day at ${parsed.time}`;
    }

    if (parsed.frequency === "weekly") {
        const ordered = WEEKDAY_OPTIONS.filter((option) => parsed.weekdays.includes(option.value));
        const dayNames = ordered.map((option) => option.label).join(", ");
        return `Every ${dayNames} at ${parsed.time}`;
    }

    if (parsed.frequency === "monthly") {
        return `On day ${parsed.dayOfMonth} at ${parsed.time}`;
    }

    return "Advanced schedule";
};

export const validateRecipients = (recipients) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
    const unique = Array.from(new Set((recipients || []).map((item) => item.trim()).filter(Boolean)));
    const invalid = unique.filter((email) => !emailRegex.test(email));
    const valid = unique.filter((email) => emailRegex.test(email));

    return {
        isValid: invalid.length === 0 && valid.length > 0,
        valid,
        invalid,
    };
};

export const scheduleWeekdayOptions = WEEKDAY_OPTIONS;
